import { Button } from '@/components/Button/Button';
import type { BrokerageRole } from '@/graphql/brokerages';
import { cellParts, describeLevel, RESOURCE_GROUPS } from '@/lib/brokerage-permissions';

import styles from './PermissionGrid.module.css';

export interface PermissionGridProps {
  roles: readonly BrokerageRole[];
  canEdit: boolean;
  onEditRole: (role: BrokerageRole) => void;
}

/** Every role against every permission, each cell saying in words what it allows. */
export function PermissionGrid({ roles, canEdit, onEditRole }: PermissionGridProps) {
  return (
    <div className={styles.scroller}>
      <table className={styles.table}>
        <caption className={styles.caption}>What each role can do</caption>
        <thead>
          <tr>
            <th className={styles.corner} scope="col">
              Permission
            </th>
            {roles.map((role) => (
              <th className={styles.roleHead} key={role.id} scope="col">
                <span className={styles.roleName}>{role.name ?? 'Unnamed role'}</span>
                <span className={styles.roleMeta}>
                  {role.source === 'CLIENT'
                    ? 'Clients on their own case'
                    : role.assigneeIds.length === 1
                      ? '1 person'
                      : `${role.assigneeIds.length} people`}
                </span>
                {canEdit && role.source !== 'CLIENT' ? (
                  <Button
                    aria-label={`Edit ${role.name ?? 'role'}`}
                    onClick={() => onEditRole(role)}
                    size="sm"
                    variant="ghost"
                  >
                    Edit
                  </Button>
                ) : null}
              </th>
            ))}
          </tr>
        </thead>
        {RESOURCE_GROUPS.map((group) => (
          <tbody key={group.title}>
            <tr>
              <th className={styles.groupHead} colSpan={roles.length + 1} scope="colgroup">
                {group.title}
              </th>
            </tr>
            {group.resources.map((entry) => (
              <tr key={entry.key}>
                <th className={styles.resource} scope="row">
                  <span className={styles.resourceName}>{entry.name}</span>
                  <span className={styles.resourceMeta}>
                    View: {entry.read.toLowerCase()}. Edit: {entry.full.toLowerCase()}.
                  </span>
                </th>
                {roles.map((role) => {
                  const parts = cellParts(role.grants, entry.key);
                  return (
                    <td className={styles.cell} key={role.id}>
                      {parts.length === 0 ? (
                        <span className={styles.none}>No access</span>
                      ) : (
                        parts.map((part) => (
                          <span
                            className={`${styles.part} ${styles[part.level.toLowerCase()]}`}
                            key={part.condition}
                            title={describeLevel(entry, part.level)}
                          >
                            {part.text}
                          </span>
                        ))
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        ))}
      </table>
    </div>
  );
}
